import type { EvidenceItem, ObservationStatus } from '../types/text-mode.types';

export interface ClinicalObservationDisplayGroup {
  id: ObservationStatus;
  labelToken: string;
  items: EvidenceItem[];
}

export interface ClinicalObservationDisplayModel {
  items: EvidenceItem[];
  positiveObservations: EvidenceItem[];
  negativeObservations: EvidenceItem[];
  unknownObservations: EvidenceItem[];
  editGroups: ClinicalObservationDisplayGroup[];
  hasItems: boolean;
}

const STATUS_ONLY_VALUES = new Set(['yes', 'no', 'true', 'false', 'unknown', 'none']);
const STATUS_WORDS_WITHOUT_OPTIONS = new Set(['positive', 'negative']);

export function shouldShowObservationValue(
  item: Pick<EvidenceItem, 'value' | 'responseOptions'>,
): boolean {
  const value = String(item.value ?? '').trim().toLowerCase();
  if (STATUS_ONLY_VALUES.has(value)) return false;

  return !(STATUS_WORDS_WITHOUT_OPTIONS.has(value) && !item.responseOptions?.length);
}

export function observationUnitToken(item: Pick<EvidenceItem, 'numericOptions' | 'value'>): string {
  const value = String(item.value ?? '').trim();
  if (!value || /[a-z%°]/i.test(value)) return '';

  const unit = item.numericOptions?.unit;
  return unit ? `units.${unit}.short` : '';
}

function observationStatusRank(status: ObservationStatus | null): number {
  if (status === 'positive') return 0;
  if (status === 'negative') return 1;
  return 2;
}

export function orderClinicalObservationItems(
  items: EvidenceItem[],
  statusForItem: (item: EvidenceItem) => ObservationStatus | null,
): EvidenceItem[] {
  return items
    .map((item, index) => ({
      item,
      index,
      rank: observationStatusRank(statusForItem(item)),
    }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ item }) => item);
}

export function buildClinicalObservationDisplayModel(
  items: EvidenceItem[],
  statusForItem: (item: EvidenceItem) => ObservationStatus | null,
): ClinicalObservationDisplayModel {
  const orderedItems = orderClinicalObservationItems(items, statusForItem);
  const positiveObservations: EvidenceItem[] = [];
  const negativeObservations: EvidenceItem[] = [];
  const unknownObservations: EvidenceItem[] = [];

  orderedItems.forEach((item) => {
    const status = statusForItem(item);

    if (status === 'positive') {
      positiveObservations.push(item);
      return;
    }

    if (status === 'negative') {
      negativeObservations.push(item);
      return;
    }

    unknownObservations.push(item);
  });

  const editGroups = ([
    {
      id: 'positive',
      labelToken: 'app.assessment_mode.text.input.observation_positive',
      items: positiveObservations,
    },
    {
      id: 'negative',
      labelToken: 'app.assessment_mode.text.input.observation_negative',
      items: negativeObservations,
    },
    {
      id: 'unknown',
      labelToken: 'app.assessment_mode.text.input.option.unknown',
      items: unknownObservations,
    },
  ] as ClinicalObservationDisplayGroup[]).filter(group => group.items.length > 0);

  return {
    items: orderedItems,
    positiveObservations,
    negativeObservations,
    unknownObservations,
    editGroups,
    hasItems: orderedItems.length > 0,
  };
}
